import React, { useState } from 'react';
import FileUploadBox from '../FileUploadBox';
import { isSupportedAudioFileType } from '../../helpers/utilities';
import { toast } from 'react-toastify';
import AudioChannelPlayer from '../AudioChannelPlayer';
import './JobChannelSelector.scss';
import RadioControl from '../RadioControl';
import { JobAudioErrorType } from './JobAudioErrorPage';
import { useHistory } from 'react-router';
import { AudioChannelSetting } from '../../models/dto/audioChannelSetting';

export default function JobChannelSelector(props: Props) {
	const [state, setState] = useState<State>({ type: 'selectFirstFile' });
	const history = useHistory();
	
	if (state.type === 'selectFirstFile') {
		return (
			<div className="page-container">
				<h1 className="h1-mb-1 text-center">First, help Creovai understand who is on your calls</h1>
				<div className="job-channel-selector__skip-file-selection">Before uploading your calls, please select one call to help Creovai identify which audio channels your customers and agents are on.</div>
				<div className="mb-4">
					<FileUploadBox onFilesSelected={onFilesSelected}
								   header="Drag and drop your file here or browse:"
								   subHeader="Only mp3 or wav files allowed. Max 200mb per file."
								   accept=".wav, .mp3" />
				</div>
			</div>
		);
	}
	
	return (
		<div className="page-container">
			<h1 className="h1-mb-1 text-center">Next, listen to the audio clip and verify the speaker(s). </h1>
			
			<div className="job-channel-selector__skip-file-selection">
				{state.channel === 1
					? `Listen to the first audio channel to verify who is speaking.`
					: `Let's try the second audio channel instead.`
				}
			</div>
			
			<div className="flex flex-align-center mb-6">
				<div className="flex-item-growing-shrinking">
					<h3 className="ellipsis">Who do you hear speaking in this audio clip?</h3>
				</div>
				<div className="flex-item-static">
					<span role="button" className="link mr-7" onClick={chooseADifferentFile}>Choose a different file</span>
				</div>
				<div className="flex-item-static">
					<button className="btn" disabled={state.participant == null || props.isChannelRequestPending} onClick={confirmParticipantMapping}>Continue</button>
				</div>
			</div>

			<div className="mb-5">
				<AudioChannelPlayer file={state.file} channel={state.channel} width="50%" />
			</div>

			<div className="mb-2"><RadioControl label="Agent" selected={state.participant === Participant.Agent} onSelected={() => chooseParticipant(Participant.Agent)} /></div>
			<div className="mb-2"><RadioControl label="Customer" selected={state.participant === Participant.Customer} onSelected={() => chooseParticipant(Participant.Customer)} /></div>
			<div className="mb-2"><RadioControl label="Both agent and customer" selected={state.participant === Participant.Both} onSelected={() => chooseParticipant(Participant.Both)} /></div>
			<div className="mb-2"><RadioControl label="Not sure" selected={state.participant === Participant.NotSure} onSelected={() => chooseParticipant(Participant.NotSure)} /></div>
		</div>
	);
	
	function confirmParticipantMapping(): void {
		if (state.type !== 'mapFirstFile')
			return;
		
		if (state.participant == null)
			return;
		
		if (state.participant === Participant.NotSure) {
			if (state.channel === 1) {
				setState({ ...state, participant: null, channel: 2 });
			} else {
				goToErrorPage('undetermined');
			}
		} else {
			const result = getChannel1Type(state.channel, state.participant);
			if (result.type === 'invalid') {
				goToErrorPage('unsupported');
			} else {
				commitChannelMapping(result.participant);
			}
		}
	}

	/**
	 * go the audio error page. we're doing this rather
	 * than having an error state in this component so that
	 * they can use the back button to 'try again.'
	 */
	function goToErrorPage(error: JobAudioErrorType): void {
		let query = `type=${error}`;
		
		if (props.jobId != null) {
			query += `&jobId=${props.jobId}`;	
		}
		
		history.push(`/engagements/${props.engagementId}/job-audio-error?${query}`);
	}
	
	function getAudioChannelSetting(channel1Type: ValidParticipant): AudioChannelSetting {
		switch (channel1Type) {
			case Participant.Agent:
				return AudioChannelSetting.Agent0Customer1;
			case Participant.Customer:
				return AudioChannelSetting.Agent1Customer0;
			case Participant.Both:
				return AudioChannelSetting.Mono;
		}
	}
	
	function commitChannelMapping(channel1Type: ValidParticipant): void {
		props.onChannelsMapped(getAudioChannelSetting(channel1Type));
	}
	
	function getChannel1Type(channel: 1 | 2, type: ValidParticipant): { type: 'valid'; participant: ValidParticipant; } | { type: 'invalid'; } {
		if (channel === 1) {
			return { type: 'valid', participant: type };
		} else {
			return type === Participant.Customer
				? { type: 'valid', participant: Participant.Agent }
				: type === Participant.Agent
					? { type: 'valid', participant: Participant.Customer }
					: { type: 'invalid' };
		}
	}
	
	function chooseADifferentFile(): void {
		setState({
			type: 'selectFirstFile'
		});
	}
	
	function onFilesSelected(files: File[]): void {
		const file = files[0];
		if (!file)
			return;
		
		if (!isSupportedAudioFileType(file.type)) {
			toast.error('Audio format for the selected file is not supported.');
			return;
		}
		
		if (files.length) {
			setState({
				type: 'mapFirstFile',
				channel: 1,
				file,
				participant: null
			});
		}
	}
	
	function chooseParticipant(participant: Participant): void {
		if (state.type === 'mapFirstFile') {
			setState({
				...state,
				participant
			});
		}
	}
}

interface Props {
	engagementId: number;
	jobId: number | null;
    onChannelsMapped: (audioChannel: AudioChannelSetting) => void;
    isChannelRequestPending: boolean;
}

interface SelectFirstFileState {
	type: 'selectFirstFile';
}

interface MapFirstFileState {
	type: 'mapFirstFile';
	file: File;
	channel: 1 | 2;
	participant: Participant | null;
}

type State = SelectFirstFileState | MapFirstFileState;

enum Participant {
	Agent = 'Agent',
	Customer = 'Customer',
	Both = 'Both',
	NotSure = 'NotSure'
}

type ValidParticipant = Participant.Agent | Participant.Customer | Participant.Both;
